import React, { useState, useEffect, useRef, useCallback, Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { DarkModeProvider } from './components/DarkModeContext';
import useAnalytics from './hooks/useAnalytics';

// --- LAZY IMPORTS ---
const Home = lazy(() => import('./pages/Home'));
const About = lazy(() => import('./pages/AboutPage'));
const Login = lazy(() => import('./pages/Login'));
const BlogList = lazy(() => import('./pages/BlogList'));
const BlogArticle = lazy(() => import('./pages/BlogArticle'));
const PublicQuizPage = lazy(() => import ('./pages/PublicQuizPage'));
const LegalPage = lazy(() => import('./pages/LegalPage'));

// Dashboards
const AdminDashboard = lazy(() => import('./pages/AdminDashboard'));
const UserDashboard = lazy(() => import('./pages/UserDashboard'));

// Admin Components
const UserManagement = lazy(() => import('./components/UserManagement'));
const ContainerManager = lazy(() => import('./components/ContainerManager')); 
const DockerImageManager = lazy(() => import('./components/DockerImageManager')); 
const LeadsInbox = lazy(() => import('./pages/LeadsInbox'));
const ReportBuilder = lazy(() => import ('./pages/ReportBuilder'));
const BlogManager = lazy(() => import('./components/BlogManager'));
const QuizManager = lazy(() =>  import('./components/quiz/QuizManager'));
const SocialStudio = lazy(() => import('./pages/SocialStudio')); 
const PitchViewer = lazy(() => import('./pages/PitchViewer')); 
const SavedCarousels = lazy(() => import('./components/carousel/SavedCarousels'));
const PresentationStudio = lazy(() => import('./pages/PresentationStudio')); 
const SavedPresentations = lazy(() => import('./components/presentation/SavedPresentations'));


// Inside your <Routes>
<Route path="/carousels" element={<SavedCarousels />} />

const AnalyticsDashboard = lazy(() => import('./components/AnalyticsDashboard')); 
const TrafficIntelligence = lazy(() => import('./components/TrafficIntelligence'));
const ContentAnalytics = lazy(() => import('./components/ContentAnalytics'));
const ContentIntelligence = lazy(() => import('./components/ContentIntelligence')); 

// User Features
const Dummy = lazy(() => import('./components/Dummy'));
const Chat = lazy(() => import('./pages/Chat'));
const Qwen7BChat = lazy(() => import('./pages/chat/Qwen7BChat'));
const Qwen32BChat = lazy(() => import('./pages/chat/Qwen32BChat'));
const EffieChat = lazy(() => import('./pages/chat/EffieChat'));
const FileUpload = lazy(() => import('./pages/FileUpload'));
const VoiceCloning = lazy(() => import('./components/VoiceCloning'));
const ImageGeneration = lazy(() => import('./components/ImageGeneration'));
const ProfileSettings  = lazy(() => import('./pages/ProfileSettings'));

const GlobalAnalytics = () => {
  useAnalytics();
  return null;
};

const PageLoader = () => (
  <div className="flex flex-col items-center justify-center min-h-screen bg-slate-50 dark:bg-slate-900 transition-colors">
    <div className="w-12 h-12 border-4 border-slate-200 border-t-blue-600 rounded-full animate-spin"></div>
    <p className="mt-4 text-slate-500 text-sm font-medium animate-pulse">Loading Application...</p>
  </div>
);

function AppContent() {
  const [token, setToken] = useState(null);
  const [role, setRole] = useState(null);
  const [username, setUsername] = useState(null);
  
  // 1. ADDED: Auth loading state acts as a gatekeeper
  const [isAuthLoading, setIsAuthLoading] = useState(true); 
  
  const idleTimer = useRef(null);
  const IDLE_TIMEOUT = 15 * 60 * 1000; 

  const logout = () => {
    localStorage.clear();
    setToken(null);
    setRole(null);
    setUsername(null); 
  };

  const handleIdleLogout = () => {
    logout();
  };

  const resetIdleTimer = useCallback(() => {
    if (idleTimer.current) clearTimeout(idleTimer.current);
    idleTimer.current = setTimeout(() => {
      handleIdleLogout();
    }, IDLE_TIMEOUT);
  }, [IDLE_TIMEOUT]);

  // Decode token to get the TRUE role
  useEffect(() => {
    const storedToken = localStorage.getItem('token');
    
    if (storedToken) {
      try {
        // Decode the JWT Payload (Base64)
        const payload = JSON.parse(atob(storedToken.split('.')[1]));
        
        // SOURCE OF TRUTH: Trust the token, not localStorage 'role'
        setToken(storedToken); // Set token here to ensure synchronicity
        setRole(payload.role); 
        setUsername(payload.sub);
        
        // Optional: Sync localStorage if it was wrong
        localStorage.setItem('role', payload.role);
        localStorage.setItem('username', payload.sub);
      } catch (e) {
        console.error("Invalid Token Format", e);
        logout(); // Force logout if token is corrupted
      }
    }

    // 2. ADDED: We have finished checking localStorage, so unlock the router
    setIsAuthLoading(false); 

    const events = ['mousemove', 'keydown', 'scroll', 'click'];
    events.forEach(event => window.addEventListener(event, resetIdleTimer));
    resetIdleTimer();

    return () => {
      events.forEach(event => window.removeEventListener(event, resetIdleTimer));
      if (idleTimer.current) clearTimeout(idleTimer.current);
    };
  }, [resetIdleTimer]);

  // 3. ADDED: If we are still checking auth, do not evaluate routes!
  if (isAuthLoading) {
    return <PageLoader />;
  }

  return (
    <>
      <GlobalAnalytics />
      <Suspense fallback={<PageLoader />}>
        <Routes>
          
          {/* 1. PUBLIC ROUTES */}
          <Route path="/" element={<Home />} />
          <Route path="/about" element={<About />} />
          <Route path="/blogs" element={<BlogList />} />
          <Route path="/blogs/:id" element={<BlogArticle />} />
          <Route path="/quiz/:slug" element={<PublicQuizPage />} />
          <Route path="/legal" element={<LegalPage />} />
          
          <Route path="/pitch/:id" element={<PitchViewer />} />
          
          {/* 2. GUEST ONLY ROUTE */}
          <Route 
            path="/login" 
            element={
              token ? <Navigate to="/dashboard" replace /> : 
              <Login setToken={setToken} setRole={setRole} setUsername={setUsername} />
            } 
          />

          {/* 3. ADMIN & AUTHOR ROUTES (The Advanced Dashboard) */}
          {token && (role === 'admin' || role === 'author') && (
             <Route path="/dashboard/*" element={<AdminDashboard username={username} role={role} logout={logout} />}>
                <Route index element={<TrafficIntelligence token={token} />} />
                <Route path="analytics" element={<AnalyticsDashboard token={token} />} />
                <Route path="intelligence" element={<ContentIntelligence token={token} />} />
                <Route path="carousels" element={<SavedCarousels />} />

                {/* Sub-routes */}
                <Route path="users" element={<UserManagement token={token} />} />
                <Route path="settings" element={<ProfileSettings token={token} />} />
                <Route path="containers" element={<ContainerManager token={token} />} />
                <Route path="docker-images" element={<DockerImageManager token={token} />} />

                <Route path="leads" element={<LeadsInbox />} />
                
                {/* --- STUDIO ROUTES --- */}
                <Route path="studio/*" element={<BlogManager />} />
                <Route path="studio/social/:id" element={<SocialStudio token={token} />} /> 
                <Route path="presentations" element={<SavedPresentations />} />
                <Route path="studio/presentation/:id" element={<PresentationStudio token={token} />} /> 

                {/* --------------------- */}
                
                <Route path="quizzes"  element={<QuizManager />} />
                <Route path="report-builder" element={<ReportBuilder />} />

                <Route path="chat/qwen7b" element={<Qwen7BChat />} />
             </Route>
          )}

          {/* 4. ORDINARY USER ROUTES (Strictly for 'user' role) */}
          {token && role === 'user' && (
             <Route path="/dashboard/*" element={<UserDashboard username={username} role={role} token={token} logout={logout} />} >
                <Route path="dummy" element={<Dummy />} />  
                <Route path="chat" element={<Chat />} /> 
                <Route path="chat/effie" element={<EffieChat />} /> 
                <Route path="chat/qwen7b" element={<Qwen7BChat />} /> 
                <Route path="chat/qwen32b" element={<Qwen32BChat />} /> 
                <Route path="file_upload" element={<FileUpload />} /> 
                <Route path="clone-voice" element={<VoiceCloning />} />
                <Route path="generate-image" element={<ImageGeneration />} />
             </Route>
          )}

          {/* 5. CATCH ALL / 404 STATE */}
          {/* We no longer need a complex loader check here because isAuthLoading handles it */}
          <Route path="*" element={
            token ? <Navigate to="/dashboard" replace /> : <Navigate to="/" replace />
          } />

        </Routes>
      </Suspense>
    </>
  );
}

function App() {
  return (
    <HelmetProvider>
      <DarkModeProvider>
        <Router>
          <AppContent />
        </Router>
      </DarkModeProvider>
    </HelmetProvider>
  );
}

export default App;